import React, { useContext } from "react";
import { Button } from "../ui/button";
import { useCreditOverview } from "../../apiHooks/useCreditOverview";
import SharedDataContext from "../../SharedDataContext";
import { useStoreBilling } from "../../stores/useStoreBIlling";
import { Skeleton } from "../ui/skeleton";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import TooltipWrapper from "../TooltipWrapper";
import { useSubscriptions } from "../../apiHooks/useSubscriptions";
import AlertTag from "../Alerts/Alert";
import AlertBold from "../Alerts/AlertBold";
import { TriangleAlertIcon } from "lucide-react";

export default function BillingOverview({ clickUpgrade }) {
  const userData = useContext(SharedDataContext);
  const { activeWorkspace } = userData || {};
  const { isLoading } = useCreditOverview(activeWorkspace?.id);
  const { creditOverview, trials } = useStoreBilling();
  const primaryTrial = trials && trials.length > 0 ? trials[0] : null;
  const expiresAt = primaryTrial?.expires_at ? new Date(primaryTrial.expires_at) : null;
  const now = new Date();
  const msInDay = 24 * 60 * 60 * 1000;
  const daysUntilExpiry = expiresAt ? Math.ceil((expiresAt - now) / msInDay) : null;
  const isExpired = !!expiresAt && expiresAt <= now;
  const isSevenDayWarning =
    typeof daysUntilExpiry === "number" && daysUntilExpiry > 0 && daysUntilExpiry <= 7;
  const consumedCredits = Number(creditOverview?.summary?.consumed) || 0;
  const totalCredits = Number(creditOverview?.summary?.total) || 0;
  const consumptionPercent = totalCredits > 0 ? Math.min((consumedCredits / totalCredits) * 100, 100) : 0;
  console.log("creditOverview", creditOverview);
  const {} = useSubscriptions(activeWorkspace?.id);

  return (
    <>
      <div className="space-y-5 mb-6">
        {creditOverview &&
          creditOverview.summary &&
          creditOverview.summary.total > 0 &&
          creditOverview.summary.available === 0 && (
            <AlertBold
              bgColor="bg-red-100"
              borderColor="border-red-500"
              headingColor="text-red-700"
              descriptionColor="text-red-700"
              icon="error.svg"
              heading="You have run out of credits!"
              description="To continue accessing the platform, upgrade your plan or purchase one time credits."
            />
          )}
        {/* <AlertBold
          bgColor="bg-warning-100"
          borderColor="border-warning-500"
          headingColor="text-warning-700"
          descriptionColor="text-warning-700"
          icon="payment-warning.svg"
          heading="There was an issue with one of your payment methods"
          description="Update your payment method to retry the payment"
        /> */}
        {primaryTrial && primaryTrial.status !== "Canceled" && (
          <>
            {isExpired ? (
              <AlertBold
                bgColor="bg-red-100"
                borderColor="border-red-500"
                headingColor="text-red-700"
                descriptionColor="text-red-700"
                icon="error.svg"
                heading="Your trial has been expired."
                description={`Subscribe to a plan to keep access to all of ${process.env.REACT_APP_APP_NAME}'s features`}
              />
            ) : (
              isSevenDayWarning && (
                <AlertBold
                  bgColor="bg-purple-700"
                  borderColor="border-purple-500"
                  headingColor="text-background"
                  descriptionColor="text-background"
                  icon="timer.svg"
                  heading={`Your trial is ending in ${daysUntilExpiry} ${daysUntilExpiry === 1 ? "day" : "days"}`}
                  description={`Subscribe to a plan to keep access to all of ${process.env.REACT_APP_APP_NAME}'s features`}
                />
              )
            )}
          </>
        )}
      </div>
      <div className="leading-5 border-border border flex h-full w-full items-center justify-between bg-background py-4 px-6 rounded-xl">
        <div>
          <p className="mb-1.5">Credits Available</p>

          {isLoading ? (
            <Skeleton className="h-8 w-[100px]" />
          ) : (
            <div className="inline-flex items-center space-x-2">
              <span className="text-2xl font-bold">
                {creditOverview &&
                creditOverview.summary &&
                creditOverview.summary.total > 0 &&
                creditOverview.summary.available === 0 ? (
                  <span className="inline-flex items-center text-red-600 space-x-2">
                    <span>0</span> <TriangleAlertIcon />
                  </span>
                ) : (
                  <> {creditOverview?.summary?.available || "-"}</>
                )}
              </span>
              {creditOverview?.reserved?.total > 0 && (
                <TooltipWrapper
                  position="right"
                  contentClassName="max-w-[300px]"
                  content={
                    <>
                      <span className="text-sm font-bold block mb-1">
                        {creditOverview?.reserved?.total} credits are on hold
                      </span>
                      <p>
                        Some credit consuming operations are in progress. Credits will be finalised after all
                        operations are completed.
                      </p>
                    </>
                  }
                >
                  <InfoCircledIcon width={18} height={18} />
                </TooltipWrapper>
              )}
            </div>
          )}
        </div>
        <div className="w-[350px]">
          <p className="mb-1.5">Consumption</p>
          <div className="rounded-full w-full h-2 bg-gray-200 relative my-3 overflow-hidden">
            <span
              className="absolute left-0 top-0 bg-brand-700 h-full rounded-full"
              style={{ width: `${consumptionPercent}%` }}
            ></span>
          </div>

          {isLoading ? (
            <Skeleton className="h-8 w-full" />
          ) : (
            <>
              {creditOverview?.summary?.total > 0 && (
                <p className="">
                  {creditOverview?.summary?.consumed} used / {creditOverview?.summary?.total} total credits
                </p>
              )}
            </>
          )}
        </div>
        <div className="">
          <Button onClick={clickUpgrade}>
            <svg width="16" height="20" viewBox="0 0 16 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M8.83354 1.66675L1.41142 10.5733C1.12075 10.9221 0.975413 11.0965 0.973192 11.2438C0.971261 11.3718 1.02832 11.4937 1.12792 11.5742C1.2425 11.6667 1.46952 11.6667 1.92357 11.6667H8.00021L7.16688 18.3334L14.589 9.42687C14.8797 9.07806 15.025 8.90366 15.0272 8.75636C15.0292 8.62832 14.9721 8.50649 14.8725 8.426C14.7579 8.33342 14.5309 8.33342 14.0768 8.33342H8.00021L8.83354 1.66675Z"
                stroke="currentColor"
                stroke-width="1.66667"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <span className="ml-2">Upgrade plan</span>
          </Button>
        </div>
      </div>
    </>
  );
}
